import { createSlice } from "@reduxjs/toolkit";

// Artifact interactivo generado por el bot (tool create_artifact del
// loop agéntico). El backend lo manda como activity de tipo "event"
// (name: "luca/artifact", value: { title, artifactType, html,
// artifactId, version, sessionId }); el store middleware de App.tsx lo
// guarda aquí y ArtifactPanel lo renderiza en un panel lateral dentro
// de un iframe sandboxed.
//
// VERSIONES: cada artifact guardado en Cosmos es una versión (1..N) de
// la conversación. `versions` es el catálogo (sin HTML, viene de
// /api/artifactVersions/<sessionId>) que el panel navega con flechas;
// `htmlByArtifactId` cachea los HTML ya descargados.
//
// isLoading se enciende cuando el evento de progreso "luca/progress"
// reporta el paso create_artifact: el panel se abre de inmediato con
// una animación de skeleton/ghost (variante aleatoria) mientras el
// artifact se genera. El evento luca/artifact la apaga siempre: con
// html trae el artifact; con html vacío significa que la generación
// falló (no se avisa al usuario; se conserva el artifact anterior si
// existía).
//
// pollUntil (epoch ms): al RETOMAR una conversación se abre una
// ventana de polling a /api/artifactVersions — si esa conversación
// tenía una generación en curso al salir, la versión nueva aparece en
// el panel en cuanto el backend la persista.
const SKELETON_VARIANTS = 4;
const RESUME_POLL_WINDOW_MS = 3 * 60 * 1000;

// Las versiones son strings con ramas ("3", "2.1"): rango numérico
// para ordenar 1, 2, 2.1, 2.2, 3...
export const versionRank = (value: any) => {
    const parts = String(value ?? "0").split(".");
    const major = parseInt(parts[0], 10) || 0;
    const minor = parts.length > 1 ? parseInt(parts[1], 10) || 0 : 0;
    return major * 100000 + minor;
};

const initialState = {
    // Sesión dueña de las versiones (viene en cada evento o del resume).
    sessionId: "",
    // Catálogo ordenado: [{artifactId, version, title, artifactType, description}]
    versions: [] as any[],
    // Cache de HTMLs por artifactId (evita re-descargar al navegar).
    htmlByArtifactId: {} as Record<string, string>,
    // Versión actualmente mostrada.
    title: "",
    artifactType: "",
    html: "",
    // Id del documento en Cosmos: arma el enlace compartible
    // `${origin}/artifact/${artifactId}` (ruta de app.py).
    artifactId: "",
    // Las versiones son strings porque pueden traer rama ("2.1").
    version: null as string | null,
    // ¿El artifact mostrado es de la conversación en la que está el
    // usuario? Sólo entonces el panel deja el botón flotante para
    // reabrirlo: una conversación nueva (o una sin artifacts) no debe
    // mostrarlo, y un artifact abierto desde la biblioteca del panel
    // lateral pertenece a OTRA conversación.
    belongsToConversation: false,
    // Artifact que el usuario eligió en la biblioteca del panel lateral
    // y que se abre junto con SU conversación: mientras esté pendiente,
    // el restore automático de la conversación (que trae la ÚLTIMA
    // versión) no lo pisa.
    preferredArtifactId: "",
    isOpen: false,
    isFullscreen: false,
    isLoading: false,
    skeletonVariant: 0,
    pollUntil: 0,
}

const rememberVersion = (state: any, payload: any) => {
    if (!payload?.artifactId || payload?.version == null) return;
    const entry = {
        artifactId: payload.artifactId,
        version: String(payload.version),
        title: payload.title || "Luca artifact",
        artifactType: payload.artifactType || "auto",
        description: payload.description || "",
        createdAt: payload.createdAt || "",
    };
    const existing = state.versions.findIndex(
        (item: any) => String(item.version) === entry.version
    );
    if (existing >= 0) {
        state.versions[existing] = entry;
    } else {
        state.versions.push(entry);
        state.versions.sort(
            (a: any, b: any) => versionRank(a.version) - versionRank(b.version)
        );
    }
};

const artifactsSlice = createSlice({
    name: 'artifacts',
    initialState,
    reducers: {
        setArtifactLoading: {
            reducer: (state, action: any) => {
                if (!state.belongsToConversation) {
                    // Lo que está en pantalla vino de la biblioteca del
                    // panel lateral (otra conversación): no puede
                    // quedarse como "el artifact anterior" de este
                    // turno — se descarta para que salga el skeleton y
                    // para que un fallo de generación no deje colgado
                    // un artifact ajeno a esta conversación.
                    state.sessionId = "";
                    state.versions = [];
                    state.htmlByArtifactId = {};
                    state.title = "";
                    state.artifactType = "";
                    state.html = "";
                    state.artifactId = "";
                    state.version = null;
                    state.pollUntil = 0;
                }
                state.isLoading = true;
                state.isOpen = true;
                // Se está generando en la conversación actual.
                state.belongsToConversation = true;
                state.skeletonVariant = action.payload.variant;
            },
            // Variante aleatoria de skeleton para que la animación de
            // "aquí va a aparecer tu artifact" no sea siempre la misma.
            prepare: () => ({
                payload: {
                    variant: Math.floor(Math.random() * SKELETON_VARIANTS),
                },
            }),
        },
        // Al retomar una conversación: fija la sesión y abre la ventana
        // de polling por si había una generación en curso. Si la
        // conversación se abrió desde la biblioteca de artifacts, el
        // payload trae además la versión exacta que hay que mostrar
        // (preferredArtifactId) en vez de la última de la conversación.
        setArtifactSession: (state, action) => {
            const sessionId = action.payload?.sessionId || "";
            if (!sessionId) return;
            if (state.sessionId !== sessionId) {
                // La conversación que se retoma no es la dueña de lo
                // que hay en pantalla (otra sesión, o un artifact
                // abierto desde la biblioteca): se descarta. Si esta
                // conversación tiene artifacts, el panel los restaura
                // con /api/artifactVersions; si no, no queda botón.
                state.versions = [];
                state.htmlByArtifactId = {};
                state.title = "";
                state.artifactType = "";
                state.html = "";
                state.artifactId = "";
                state.version = null;
                state.belongsToConversation = false;
                state.isOpen = false;
                state.isFullscreen = false;
                state.isLoading = false;
            }
            state.sessionId = sessionId;
            state.preferredArtifactId = action.payload?.preferredArtifactId || "";
            state.pollUntil = Date.now() + RESUME_POLL_WINDOW_MS;
        },
        setArtifact: (state, action) => {
            const html = action.payload?.html || "";
            // Artifact RESTAURADO (resume de conversación): se carga el
            // estado pero el panel NO se abre solo — queda el botón y
            // el usuario decide. La ventana de polling sigue viva por
            // si había una generación en curso al salir.
            const restored = !!action.payload?.restored;
            state.isLoading = false;
            if (
                restored &&
                state.preferredArtifactId &&
                action.payload?.artifactId !== state.preferredArtifactId
            ) {
                // El usuario abrió una versión concreta desde la
                // biblioteca: el restore automático de la conversación
                // (bot.py re-inyecta el último artifact_code) no la pisa.
                return;
            }
            if (action.payload?.sessionId) {
                state.sessionId = action.payload.sessionId;
            }
            if (!html) {
                // Generación fallida: se conserva el artifact previo si
                // lo había; si no, se cierra el panel sin avisar (mismo
                // criterio que los fallos de gráficas).
                if (!state.html) {
                    state.isOpen = false;
                    state.isFullscreen = false;
                }
                return;
            }
            state.title = action.payload?.title || "Luca artifact";
            state.artifactType = action.payload?.artifactType || "auto";
            state.html = html;
            state.artifactId = action.payload?.artifactId || "";
            state.version =
                action.payload?.version != null
                    ? String(action.payload.version)
                    : null;
            // Recién generado o restaurado al retomar: en los dos casos
            // es el artifact de la conversación en curso.
            state.belongsToConversation = true;
            if (state.artifactId) {
                state.htmlByArtifactId[state.artifactId] = html;
            }
            rememberVersion(state, action.payload);
            if (!restored) {
                // Llegó un artifact recién generado, o el que el
                // usuario eligió en la biblioteca: cierra el polling y
                // abre (o re-abre) el panel.
                state.preferredArtifactId = "";
                state.pollUntil = 0;
                state.isOpen = true;
            }
        },
        // Catálogo completo desde /api/artifactVersions/<sessionId>.
        setArtifactVersions: (state, action) => {
            const list = Array.isArray(action.payload) ? action.payload : [];
            state.versions = list
                .filter((item: any) => item?.artifactId && item?.version != null)
                .map((item: any) => ({ ...item, version: String(item.version) }))
                .sort(
                    (a: any, b: any) =>
                        versionRank(a.version) - versionRank(b.version)
                );
        },
        // Artifact abierto desde la BIBLIOTECA del panel lateral
        // (sección "Artifacts"): puede ser de cualquier conversación
        // del usuario, así que se muestra en el panel pero NO se marca
        // como artifact de la conversación en curso — al cerrarlo no
        // queda el botón flotante. El catálogo de versiones lo recarga
        // el panel al ver la sesión nueva.
        openStoredArtifact: (state, action) => {
            const html = action.payload?.html || "";
            if (!html) return;
            const sessionId = action.payload?.sessionId || "";
            if (sessionId !== state.sessionId) {
                state.versions = [];
                state.htmlByArtifactId = {};
                state.belongsToConversation = false;
                state.sessionId = sessionId;
            }
            state.title = action.payload?.title || "Luca artifact";
            state.artifactType = action.payload?.artifactType || "auto";
            state.html = html;
            state.artifactId = action.payload?.artifactId || "";
            state.version =
                action.payload?.version != null
                    ? String(action.payload.version)
                    : null;
            if (state.artifactId) {
                state.htmlByArtifactId[state.artifactId] = html;
            }
            rememberVersion(state, action.payload);
            // Abrir uno viejo no debe dejar corriendo el polling de la
            // conversación anterior.
            state.pollUntil = 0;
            state.isLoading = false;
            state.isOpen = true;
        },
        // Navegación con flechas: muestra una versión ya descargada.
        showArtifactVersion: (state, action) => {
            const html = action.payload?.html || "";
            if (!html) return;
            state.title = action.payload?.title || "Luca artifact";
            state.artifactType = action.payload?.artifactType || "auto";
            state.html = html;
            state.artifactId = action.payload?.artifactId || "";
            state.version = action.payload?.version ?? null;
            if (state.artifactId) {
                state.htmlByArtifactId[state.artifactId] = html;
            }
            state.isOpen = true;
        },
        stopArtifactPolling: (state) => {
            state.pollUntil = 0;
        },
        openArtifactPanel: (state) => {
            state.isOpen = !!state.html || state.isLoading;
        },
        closeArtifactPanel: (state) => {
            state.isOpen = false;
            state.isFullscreen = false;
        },
        toggleArtifactFullscreen: (state) => {
            state.isFullscreen = !state.isFullscreen;
        },
        exitArtifactFullscreen: (state) => {
            state.isFullscreen = false;
        },
        clearArtifact: (state) => {
            state.sessionId = "";
            state.versions = [];
            state.htmlByArtifactId = {};
            state.title = "";
            state.artifactType = "";
            state.html = "";
            state.artifactId = "";
            state.version = null;
            state.belongsToConversation = false;
            state.preferredArtifactId = "";
            state.isOpen = false;
            state.isFullscreen = false;
            state.isLoading = false;
            state.pollUntil = 0;
        }
    }
})

export const {
    setArtifactLoading,
    setArtifactSession,
    setArtifact,
    setArtifactVersions,
    openStoredArtifact,
    showArtifactVersion,
    stopArtifactPolling,
    openArtifactPanel,
    closeArtifactPanel,
    toggleArtifactFullscreen,
    exitArtifactFullscreen,
    clearArtifact,
} = artifactsSlice.actions;
export default artifactsSlice.reducer
